import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { RefreshCw, Trash2, Plug, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useIntegrations,
  useConnectIntegration,
  useDisconnectIntegration,
  useSyncIntegration
} from '@/hooks/use-integrations';

export function IntegrationsTab() {
  const { data, isLoading } = useIntegrations();
  const connect = useConnectIntegration();
  const disconnect = useDisconnectIntegration();
  const sync = useSyncIntegration();

  const [newSource, setNewSource] = useState('teamtailor');
  const [apiKey, setApiKey] = useState('');

  const integrations = data?.data ?? [];

  const handleConnect = () => {
    if (!apiKey.trim()) return;
    connect.mutate(
      { source: newSource, credentials: { apiKey: apiKey.trim() } },
      { onSuccess: () => setApiKey('') }
    );
  };

  return (
    <div className="space-y-6">
      {/* Connected integrations */}
      <div>
        <h3 className="mb-3 text-sm font-semibold">Connected Integrations</h3>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : integrations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No integrations connected yet.</p>
        ) : (
          <div className="space-y-3">
            {integrations.map((integration) => (
              <Card key={integration.id}>
                <CardContent className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-sm font-medium capitalize">{integration.source}</p>
                    <p className="text-xs text-muted-foreground">
                      {integration.lastSynced
                        ? `Last synced ${formatDistanceToNow(new Date(integration.lastSynced), { addSuffix: true })}`
                        : 'Never synced'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => sync.mutate(integration.source)}
                      disabled={sync.isPending}
                    >
                      {sync.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => disconnect.mutate(integration.source)}
                      disabled={disconnect.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Connect new integration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Plug className="h-4 w-4" />
            Connect Integration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row">
            <select
              value={newSource}
              onChange={(e) => setNewSource(e.target.value)}
              className="h-9 rounded-md border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="teamtailor">Teamtailor</option>
              <option value="cinode">Cinode</option>
            </select>
            <input
              type="password"
              placeholder="API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="h-9 flex-1 rounded-md border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
            <Button
              size="sm"
              onClick={handleConnect}
              disabled={connect.isPending || !apiKey.trim()}
            >
              {connect.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Connect
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
